/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { SEO } from "@/components/SEO";
import { SpecLabel } from "@/components/Section";

export default function AcceptableUsePolicyPage() {
    return (
        <>
            <SEO
                title="Acceptable Use Policy"
                description="Elevara Systems acceptable use policy — what's allowed and prohibited when using our marketing systems, messaging tools, and platform. Last updated June 8, 2026."
                path="/acceptable-use"
                noindex
            />
            <div
                className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16"
                data-testid="acceptable-use-page"
            >
                <SpecLabel>Legal</SpecLabel>
                <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#0a0b0d]">
                    Acceptable Use Policy
                </h1>
                <p className="mt-3 text-sm text-[#4a5562] font-display font-bold uppercase tracking-[0.18em]">
                    Last updated: June 8, 2026
                </p>
            </div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                <article
                    data-testid="legal-content"
                    className="rounded-2xl border-2 border-[#0a0b0d]/8 bg-white p-6 sm:p-10 lg:p-14 max-w-4xl mx-auto leading-relaxed text-[#0a0b0d]"
                >
                    <h2 className="font-display text-2xl font-extrabold mt-0">1. Purpose</h2>
                    <p className="mt-3 text-[#4a5562]">
                        This Acceptable Use Policy ("AUP") governs your use of
                        the Elevara Systems platform, including our website
                        builder, review tools, marketing automation, SMS and
                        voice services, unified inbox, and business phone
                        ("Services"). By using the Services, you agree to this
                        AUP. Violation may result in suspension or termination
                        of your account, without refund, and where appropriate
                        notification of carriers, registries, or law
                        enforcement.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">2. General Prohibited Conduct</h2>
                    <p className="mt-3 text-[#4a5562]">
                        You agree not to use the Services to:
                    </p>
                    <ul className="mt-3 space-y-2 text-[#4a5562] list-disc pl-6">
                        <li>Violate any applicable law, regulation, or third-party right.</li>
                        <li>
                            Engage in fraud, money laundering, phishing, or
                            any deceptive practice.
                        </li>
                        <li>
                            Send unsolicited, bulk, or commercial communications
                            ("spam"), in any channel — SMS, MMS, voice, email,
                            chat, or web form.
                        </li>
                        <li>
                            Transmit malware, viruses, ransomware, or any code
                            designed to disrupt, damage, or gain unauthorized
                            access to a system.
                        </li>
                        <li>
                            Probe, scan, or test the vulnerability of our
                            systems or any system on our network, or breach
                            security or authentication measures.
                        </li>
                        <li>
                            Harvest or collect contact information from the
                            Services without express consent.
                        </li>
                        <li>
                            Impersonate any person or entity, or misrepresent
                            your affiliation with a person or entity.
                        </li>
                        <li>
                            Use the Services for any activity that could
                            reasonably be expected to harm minors, harass,
                            threaten, defame, or otherwise abuse any person.
                        </li>
                    </ul>

                    <h2 className="font-display text-2xl font-extrabold mt-10">3. SMS &amp; Voice Compliance (A2P 10DLC, TCPA, CTIA, CAN-SPAM)</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Elevara Systems supports compliant business messaging.
                        When using our SMS, MMS, or voice features, you must
                        comply with all applicable laws and carrier
                        guidelines, including (without limitation) the
                        Telephone Consumer Protection Act ("TCPA"), the CTIA
                        Messaging Principles and Best Practices, CAN-SPAM, and
                        the A2P 10DLC registration requirements.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        a. Express written consent (opt-in)
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        You may only message recipients who have provided
                        prior express written consent to receive messages from
                        your business at the specific number being messaged.
                        Consent must be evidenced, current, and specific to
                        the type of messages being sent. You must retain
                        records of consent and provide them on request.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        b. Opt-out (STOP) and help (HELP) keywords
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Every program must honor the keywords STOP, END,
                        CANCEL, UNSUBSCRIBE, and QUIT as opt-out, and HELP and
                        INFO as help requests. Opt-out must be processed
                        immediately and free of charge. Once a recipient opts
                        out, you must not message them from that number again,
                        through any platform, unless they re-opt-in.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        c. Sender identification &amp; message frequency
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        The first message of every program must identify the
                        sending business and disclose message frequency,
                        message-and-data-rates language, and a link to your
                        privacy policy and terms. Frequency must match what
                        was disclosed at opt-in.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        d. Prohibited content (SHAFT &amp; high-risk categories)
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Even with consent, you may not use SMS, MMS, or voice
                        to send or solicit content involving sex, hate,
                        alcohol, firearms, or tobacco/cannabis ("SHAFT"). You
                        also may not message content related to gambling,
                        lending, debt collection, payday loans,
                        cryptocurrency, or other regulated/high-risk
                        categories unless you have received Elevara's
                        documented written approval and have completed all
                        carrier-required vetting.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        e. No URL shorteners using public/shared domains
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Links in SMS/MMS messages must use your own brand
                        domain or a domain assigned to you by Elevara. Public
                        shared URL shorteners (e.g. bit.ly, tinyurl) are
                        prohibited because carriers flag them as spam.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        f. No filter evasion
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Snowshoeing, dynamic routing, content spoofing,
                        special character substitution to bypass carrier
                        filters, and any other technique designed to evade
                        spam detection are strictly prohibited.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        g. A2P 10DLC brand &amp; campaign registration
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Any messaging on US 10-digit long codes ("10DLC")
                        must be sent under a brand and campaign that you have
                        registered with The Campaign Registry through
                        Elevara. You may not begin sending until your campaign
                        is approved. You must keep your brand information,
                        opt-in flow, sample messages, and use-case current.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">4. Email Compliance (CAN-SPAM)</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Email sent through the Services must include accurate
                        sender information, a truthful subject line, a clear
                        identification that the message is an advertisement
                        where applicable, a valid physical postal address, and
                        a clear, conspicuous, and functional unsubscribe
                        mechanism that is honored within 10 business days.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">5. Content Restrictions</h2>
                    <p className="mt-3 text-[#4a5562]">
                        You will not transmit, store, or display through the
                        Services any content that is unlawful, obscene,
                        defamatory, libelous, harassing, hateful, infringing,
                        deceptive, or otherwise objectionable. Without
                        limitation, this includes:
                    </p>
                    <ul className="mt-3 space-y-2 text-[#4a5562] list-disc pl-6">
                        <li>Child sexual abuse material — zero tolerance.</li>
                        <li>Content promoting violence, terrorism, or human trafficking.</li>
                        <li>Pirated or counterfeit goods and services.</li>
                        <li>Pyramid schemes, multi-level marketing, "get rich quick" schemes.</li>
                        <li>Misleading health claims, miracle cures, or unapproved pharmaceuticals.</li>
                    </ul>

                    <h2 className="font-display text-2xl font-extrabold mt-10">6. System &amp; Network Use</h2>
                    <p className="mt-3 text-[#4a5562]">
                        You will not (a) attempt to reverse engineer, decompile,
                        or otherwise extract source code from the Services;
                        (b) use the Services to provide a substantially similar
                        product to a third party; (c) circumvent rate limits,
                        usage quotas, or access controls; or (d) use the
                        Services in any way that interferes with their
                        operation or any other customer's use.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">7. Reporting Violations</h2>
                    <p className="mt-3 text-[#4a5562]">
                        If you believe someone is using the Services in
                        violation of this AUP — including spam, abuse, or
                        unauthorized messaging — please report it to{" "}
                        <a
                            href="mailto:abuse@theelevara.com"
                            className="text-[#188bf6] font-display font-bold hover:underline"
                        >
                            abuse@theelevara.com
                        </a>{" "}
                        with as much detail as possible (the originating
                        number or email, message content, timestamps, and any
                        screenshots). We investigate every report.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">8. Enforcement</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Elevara Systems may suspend or terminate access to the
                        Services, without notice and without refund, if we
                        determine in our sole reasonable judgment that you
                        have violated this AUP. Carriers may also independently
                        suspend numbers, campaigns, or brands. In serious
                        cases, we will report violations to relevant
                        authorities. We may also pass through any fines,
                        penalties, or carrier fees that result from your
                        violation.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">9. Changes</h2>
                    <p className="mt-3 text-[#4a5562]">
                        We may revise this AUP at any time to reflect changes
                        in law, carrier rules, or our Services. The updated
                        version will be posted at theelevara.com/acceptable-use
                        with a new "Last updated" date. Material changes will
                        be communicated to account holders.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">10. Contact</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Elevara Systems |{" "}
                        <a
                            href="mailto:contact@theelevara.com"
                            className="text-[#188bf6] font-display font-bold hover:underline"
                        >
                            contact@theelevara.com
                        </a>{" "}
                        |{" "}
                        <a
                            href="tel:+19418075580"
                            className="text-[#188bf6] font-display font-bold hover:underline"
                        >
                            941-807-5580
                        </a>{" "}
                        | theelevara.com
                    </p>
                </article>
            </div>
        </>
    );
}
